<script setup>
import { ref, computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  agentData: {
    type: Array,
    default: () => [],
  },
  inboxData: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Ranking de Eficiencia',
  },
});

const activeTab = ref('agents');

const sortedAgents = computed(() => {
  return [...props.agentData]
    .sort((a, b) => b.efficiency_score - a.efficiency_score)
    .slice(0, 10);
});

const sortedInboxes = computed(() => {
  return [...props.inboxData]
    .sort((a, b) => b.efficiency_score - a.efficiency_score)
    .slice(0, 10);
});

function getScoreColor(score) {
  if (score >= 80) return 'text-n-teal-11';
  if (score >= 60) return 'text-n-blue-11';
  if (score >= 40) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getScoreBgColor(score) {
  if (score >= 80) return 'bg-n-teal-9';
  if (score >= 60) return 'bg-n-blue-9';
  if (score >= 40) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function getMedalIcon(idx) {
  if (idx === 0) return { icon: 'i-lucide-medal', color: 'text-amber-500' };
  if (idx === 1) return { icon: 'i-lucide-medal', color: 'text-slate-400' };
  if (idx === 2) return { icon: 'i-lucide-medal', color: 'text-amber-700' };
  return null;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-trophy text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex gap-1">
        <button
          class="px-3 py-1 text-xs rounded-lg transition-colors"
          :class="
            activeTab === 'agents'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'agents'"
        >
          Agentes
        </button>
        <button
          class="px-3 py-1 text-xs rounded-lg transition-colors"
          :class="
            activeTab === 'inboxes'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'inboxes'"
        >
          Canais
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Agents Tab -->
    <div v-else-if="activeTab === 'agents'" class="flex flex-col gap-2">
      <div
        v-if="sortedAgents.length === 0"
        class="flex items-center justify-center h-32 text-n-slate-11 text-sm"
      >
        Sem dados de agentes
      </div>
      <div
        v-for="(agent, idx) in sortedAgents"
        :key="agent.agent_id"
        class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
      >
        <div class="flex items-center justify-center w-6">
          <span
            v-if="getMedalIcon(idx)"
            :class="[getMedalIcon(idx).icon, getMedalIcon(idx).color]"
            class="text-lg"
          />
          <span v-else class="text-xs text-n-slate-10">{{ idx + 1 }}</span>
        </div>
        <Avatar :name="agent.agent_name" :size="32" rounded-full />
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-n-slate-12 truncate">
            {{ agent.agent_name }}
          </div>
          <div class="flex items-center gap-2 text-xs text-n-slate-10">
            <span>{{ agent.conversations }} conv.</span>
            <span>{{ agent.resolution_rate }}% resol.</span>
            <span>{{ agent.avg_response_minutes }} min resp.</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-16 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full"
              :class="getScoreBgColor(agent.efficiency_score)"
              :style="{ width: `${agent.efficiency_score}%` }"
            />
          </div>
          <span
            class="text-sm font-bold w-10 text-right"
            :class="getScoreColor(agent.efficiency_score)"
          >
            {{ agent.efficiency_score }}
          </span>
        </div>
      </div>
    </div>

    <!-- Inboxes Tab -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-if="sortedInboxes.length === 0"
        class="flex items-center justify-center h-32 text-n-slate-11 text-sm"
      >
        Sem dados de canais
      </div>
      <div
        v-for="(inbox, idx) in sortedInboxes"
        :key="inbox.inbox_id"
        class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
      >
        <div class="flex items-center justify-center w-6">
          <span
            v-if="getMedalIcon(idx)"
            :class="[getMedalIcon(idx).icon, getMedalIcon(idx).color]"
            class="text-lg"
          />
          <span v-else class="text-xs text-n-slate-10">{{ idx + 1 }}</span>
        </div>
        <span class="i-lucide-inbox text-n-blue-10" />
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-n-slate-12 truncate">
            {{ inbox.inbox_name }}
          </div>
          <div class="flex items-center gap-2 text-xs text-n-slate-10">
            <span>{{ inbox.conversations }} conv.</span>
            <span>{{ inbox.resolution_rate }}% resol.</span>
            <span>{{ inbox.sla_compliance }}% SLA</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-16 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full"
              :class="getScoreBgColor(inbox.efficiency_score)"
              :style="{ width: `${inbox.efficiency_score}%` }"
            />
          </div>
          <span
            class="text-sm font-bold w-10 text-right"
            :class="getScoreColor(inbox.efficiency_score)"
          >
            {{ inbox.efficiency_score }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
